import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import Input from "@/components/ui/input";
import Text from "@/components/ui/text";
import { LeadOwnerEmails } from "@/entities/hubspot/types";
import {
  useSaveWebFormRouting,
  webFormErrorDetail
} from "@/entities/webForm/hooks";
import { WebFormOwnerRule, WebFormSettings } from "@/entities/webForm/types";
import { OwnerSelect } from "@/pages/settings/components/integrations/hubspot/OwnerSelect";
import {
  MAX_NAME_LENGTH,
  MAX_OWNER_RULES,
  routingErrors,
  routingUpdate
} from "@/pages/settings/components/integrations/webForm/webFormOptions";
import Section from "@/pages/settings/components/sections/Section";

const PREFIX = "integrations.webFormSettings.routing";
const OWNER_ROLES: (keyof LeadOwnerEmails)[] = ["sdrOwner", "aeOwner"];
const NO_OWNERS: LeadOwnerEmails = { sdrOwner: null, aeOwner: null };
const ERROR_ID = "web-form-settings-routing-error";

const SELECT_CLASSNAME = "text-start border border-border-border2 pl-0";
const SELECT_TRIGGER_CLASSNAME = "w-full min-h-8 h-9";

interface WebFormRoutingProps {
  settings: WebFormSettings | undefined;
  isLoading: boolean;
  subTitle: string;
  // Another save is running, so this one waits.
  isBusy: boolean;
}

const savedRouting = (settings: WebFormSettings) =>
  routingUpdate(
    settings.ownerRoutingField,
    settings.ownerRules,
    settings.fallbackOwners ?? NO_OWNERS
  );

export const WebFormRouting: React.FC<WebFormRoutingProps> = ({
  settings,
  isLoading,
  subTitle,
  isBusy
}) => {
  const { t } = useTranslation();
  const saveRouting = useSaveWebFormRouting();
  const [saveError, setSaveError] = useState<string | null>(null);

  const [routingField, setRoutingField] = useState(
    settings?.ownerRoutingField ?? ""
  );
  const [rules, setRules] = useState<WebFormOwnerRule[]>(
    settings?.ownerRules ?? []
  );
  const [fallback, setFallback] = useState<LeadOwnerEmails>(
    settings?.fallbackOwners ?? NO_OWNERS
  );
  // Show what was saved once it loads or changes, replacing any older draft.
  const saved = settings ? JSON.stringify(savedRouting(settings)) : null;
  const [synced, setSynced] = useState(saved);
  if (saved !== synced) {
    setSynced(saved);
    if (settings) {
      setRoutingField(settings.ownerRoutingField);
      setRules(settings.ownerRules);
      setFallback(settings.fallbackOwners ?? NO_OWNERS);
    }
  }

  const update = routingUpdate(routingField, rules, fallback);
  const disabled = !settings || isBusy || saveRouting.isPending;
  const isDirty = saved !== null && JSON.stringify(update) !== saved;
  // An untouched form shows no errors, even when what was saved is incomplete.
  const errors = isDirty ? routingErrors(update) : [];
  const canSave = isDirty && errors.length === 0 && !disabled;

  const edit = <T,>(setter: React.Dispatch<React.SetStateAction<T>>) => {
    return (next: React.SetStateAction<T>) => {
      setter(next);
      setSaveError(null);
    };
  };
  const editRules = edit(setRules);
  const editFallback = edit(setFallback);

  const updateRule = (index: number, changes: Partial<WebFormOwnerRule>) =>
    editRules((current) =>
      current.map((rule, ruleIndex) =>
        ruleIndex === index ? { ...rule, ...changes } : rule
      )
    );

  const handleSave = () => {
    setSaveError(null);
    saveRouting.mutate(
      { data: update },
      {
        onSuccess: () => toast.success(t(`${PREFIX}.saved`)),
        onError: (error) => {
          const message = webFormErrorDetail(error) ?? t(`${PREFIX}.saveError`);
          setSaveError(message);
          toast.error(message);
        }
      }
    );
  };

  const ownerSelect = (
    fieldName: string,
    owners: LeadOwnerEmails,
    role: keyof LeadOwnerEmails,
    onChange: (email: string | null) => void
  ) => (
    <div key={role} className="flex flex-col gap-1">
      <Text variant="bodySecondary">{t(`${PREFIX}.${role}`)}</Text>
      <OwnerSelect
        fieldName={fieldName}
        email={owners[role]}
        onChange={onChange}
        placeholder={t(`${PREFIX}.unassigned`)}
        unknownUserLabel={t(`${PREFIX}.unknownUser`)}
        disabled={disabled}
        className={SELECT_CLASSNAME}
        popoverTriggerClassName={SELECT_TRIGGER_CLASSNAME}
      />
    </div>
  );

  return (
    <Section
      title={t(`${PREFIX}.title`)}
      subTitle={subTitle}
      isLoading={isLoading}
      items={[
        {
          title: t(`${PREFIX}.field.title`),
          subTitle: t(`${PREFIX}.field.subtitle`),
          customAction: (
            <Input
              data-test="web-form-settings-routing-field"
              aria-label={t(`${PREFIX}.field.title`)}
              placeholder={t(`${PREFIX}.field.placeholder`)}
              value={routingField}
              onChange={(event) => edit(setRoutingField)(event.target.value)}
              hasError={errors.includes("fieldRequired")}
              maxLength={MAX_NAME_LENGTH}
              disabled={disabled}
              containerClassName="w-[240px]"
            />
          )
        }
      ]}
    >
      <div className="flex flex-col gap-3">
        <Text variant="bodySecondary">{t(`${PREFIX}.rules.subtitle`)}</Text>
        {rules.map((rule, index) => (
          <div
            key={index}
            className="flex flex-col gap-2 rounded-lg border border-border-border1 p-3"
            data-test={`web-form-settings-rule-${index}`}
          >
            <div className="flex items-end gap-2">
              <div className="flex flex-col gap-1">
                <Text variant="bodySecondary">
                  {t(`${PREFIX}.rules.value`)}
                </Text>
                <Input
                  data-test={`web-form-settings-rule-value-${index}`}
                  aria-label={t(`${PREFIX}.a11y.ruleValue`, {
                    position: index + 1
                  })}
                  placeholder={t(`${PREFIX}.rules.valuePlaceholder`)}
                  value={rule.value}
                  onChange={(event) =>
                    updateRule(index, { value: event.target.value })
                  }
                  maxLength={MAX_NAME_LENGTH}
                  disabled={disabled}
                  containerClassName="w-[26rem]"
                />
              </div>
              <IconButton
                variant="iconNoBg"
                size="lg"
                data-test={`web-form-settings-rule-remove-${index}`}
                aria-label={t(`${PREFIX}.a11y.removeRule`, {
                  position: index + 1
                })}
                onClick={() =>
                  editRules((current) => current.filter((_, i) => i !== index))
                }
                disabled={disabled}
              >
                <Trash2 />
              </IconButton>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {OWNER_ROLES.map((role) =>
                ownerSelect(`${role}-${index}`, rule, role, (email) =>
                  updateRule(index, { [role]: email })
                )
              )}
            </div>
          </div>
        ))}
        <Button
          variant="tertiary"
          className="w-fit"
          data-test="web-form-settings-rule-add"
          onClick={() =>
            editRules((current) => [...current, { value: "", ...NO_OWNERS }])
          }
          disabled={disabled || rules.length >= MAX_OWNER_RULES}
        >
          {t(`${PREFIX}.rules.add`)}
        </Button>
      </div>
      <div
        className="flex flex-col gap-3"
        data-test="web-form-settings-fallback"
      >
        <Text variant="lgLabel">{t(`${PREFIX}.fallback.title`)}</Text>
        <Text variant="bodySecondary">{t(`${PREFIX}.fallback.subtitle`)}</Text>
        <div className="grid grid-cols-2 gap-4">
          {OWNER_ROLES.map((role) =>
            ownerSelect(`fallback-${role}`, fallback, role, (email) =>
              editFallback((current) => ({ ...current, [role]: email }))
            )
          )}
        </div>
      </div>
      {(errors.length > 0 || saveError) && (
        <div
          className="flex flex-col gap-1"
          id={ERROR_ID}
          role="alert"
          data-test="web-form-settings-routing-error"
        >
          {errors.map((error) => (
            <Text key={error} variant="smBodyError">
              {t(`${PREFIX}.errors.${error}`)}
            </Text>
          ))}
          {saveError && <Text variant="smBodyError">{saveError}</Text>}
        </div>
      )}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={!canSave}
          data-test="web-form-settings-save-routing"
        >
          {t(`${PREFIX}.save`)}
        </Button>
      </div>
    </Section>
  );
};
